import React from 'react'
import PropTypes from 'prop-types'
import { Box, IconButton, Stack, Tooltip, Typography } from '@mui/material'
import ContentCopyOutlinedIcon from '@mui/icons-material/ContentCopyOutlined'

import { theme } from '../../theme'
import { copyTextToClipboard } from '../../utils/clipboard'
import { shortenOrderNumber } from '../../utils/orderNumber'

const rowValueSx = {
  fontSize: '11px',
  lineHeight: 1.85,
  color: theme.palette.fontColor.medium,
  wordBreak: 'break-word'
}

const CopyButton = ({ value, label }) => {
  if (!value) {
    return null
  }

  return (
    <Tooltip title={label}>
      <IconButton
        size='small'
        aria-label={label}
        onClick={() => copyTextToClipboard(value)}
        sx={{
          width: 24,
          height: 24,
          color: theme.palette.fontColor.medium,
          '&:hover': { color: theme.palette.accent.main }
        }}
      >
        <ContentCopyOutlinedIcon sx={{ fontSize: 15 }} />
      </IconButton>
    </Tooltip>
  )
}

const BankTransferDetailsCard = ({ details, title, helperText }) => {
  if (!details?.configured) {
    return null
  }

  const rawReferenceValue = String(details.reference_value || '').trim()
  const referenceValue = rawReferenceValue
    ? shortenOrderNumber(rawReferenceValue)
    : 'Se genereaza dupa plasarea comenzii'

  const renderCopyRow = (label, value, copyLabel, monospace = false, copyValue = value) => (
    <Stack direction='row' spacing={0.75} alignItems='center'>
      <Typography sx={{
        ...rowValueSx,
        ...(monospace ? { fontFamily: 'monospace', letterSpacing: '0.02em' } : {}),
        flex: 1
      }}
      >
        <strong>{label}:</strong> {value || '-'}
      </Typography>
      <CopyButton value={copyValue} label={copyLabel} />
    </Stack>
  )

  return (
    <Box
      sx={{
        border: '1px solid',
        borderColor: theme.palette.accent.main,
        borderRadius: '12px',
        p: 1.5,
        backgroundColor: '#fff6f7'
      }}
    >
      <Stack spacing={0.75}>
        <Typography sx={{ fontSize: '12px', fontWeight: 700, lineHeight: 1.55 }}>
          {title}
        </Typography>
        {helperText && (
          <Typography sx={{ fontSize: '10.5px', lineHeight: 1.8, color: theme.palette.fontColor.medium }}>
            {helperText}
          </Typography>
        )}
        <Typography sx={rowValueSx}><strong>Beneficiar:</strong> {details.beneficiary}</Typography>
        {renderCopyRow('IBAN', details.iban, 'Copiaza IBAN', true)}
        <Typography sx={rowValueSx}><strong>Banca:</strong> {details.bank_name}</Typography>
        {renderCopyRow(details.reference_label, referenceValue, 'Copiaza referinta plata', false, rawReferenceValue ? referenceValue : '')}
      </Stack>
    </Box>
  )
}

CopyButton.propTypes = {
  value: PropTypes.string,
  label: PropTypes.string.isRequired
}

BankTransferDetailsCard.propTypes = {
  details: PropTypes.shape({
    configured: PropTypes.bool,
    beneficiary: PropTypes.string,
    iban: PropTypes.string,
    bank_name: PropTypes.string,
    reference_label: PropTypes.string,
    reference_value: PropTypes.string
  }),
  title: PropTypes.string,
  helperText: PropTypes.string
}

BankTransferDetailsCard.defaultProps = {
  title: 'Detalii pentru plata prin ordin de plata',
  helperText: ''
}

export default BankTransferDetailsCard
